<template>
  <div class="form__element">
    <div class="form__checkbox"
         :class="{'form__checkbox--error': hasError, 'form__checkbox--negative': !inverted}">
      <div class="form__checkbox-main-wrapper">
        <input :id="id + '-' + inputId"
                type="checkbox"
                :checked="modelValue"
                @change="handleChange"
                class="form__checkbox-field"
                :aria-invalid="hasError ? 'true' : null"
                :aria-describedby="hasError ? `${id}-error` : null"
        />
        <span class="icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" fill="currentColor" viewBox="0 0 34 34" aria-hidden="true" focusable="false">
            <path fill-rule="evenodd" d="M24.528 11.436c.627.585.63 1.534.007 2.122l-9.546 9c-.3.283-.71.442-1.135.442-.426 0-.834-.16-1.135-.442L9.465 19.49a1.435 1.435 0 0 1 .007-2.121 1.676 1.676 0 0 1 2.263.006l2.119 1.998 8.411-7.93a1.676 1.676 0 0 1 2.263-.007Z" clip-rule="evenodd"></path>
          </svg>
        </span>
        <span class="form__label">
          <label class="label label--font-family-primary"
                 :class="{'label--lg label--capitalize': helptext, 'label--sm  label--transform-unset': !helptext}"
                 :for="id + '-' + inputId"
                 v-html="label"></label>
        </span>
      </div>
      <div :id="'helptext-' + id + '-' + inputId" v-if="helptext" class="form__checkbox-helptext" v-html="helptext"></div>
    </div>
    <Error v-if="hasError"
           :inverted="inverted"
           :id="'error-' + id + '-' + inputId"
           :message="errorMessage" />
  </div>
</template>

<script setup>
import {ref, computed, watch, useId} from 'vue';
import Error from './Error.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  label: {
    required: true,
  },
  helptext: {
    default: ''
  },
  dialog: {
    type: Object,
    default: null
  },
  formatLabelHtml: {
    type: Boolean,
    default: false
  },
  modelValue: {
    type: Boolean,
    default: false,
  },
  rules: {
    type: Array,
    default: () => [],
  },
  forceValidation: {
    type: Boolean,
    default: false,
  },
  inverted: {
    type: Boolean,
    default: false,
  }
});

const emit = defineEmits(['update:modelValue', 'validation-change']);

const internalValue = ref(props.modelValue);
const isTouched = ref(false);
const errorMessage = ref('');
const inputId = useId();

watch(() => props.modelValue, (newValue) => {
  internalValue.value = newValue;
});

const validate = () => {
  for (const rule of props.rules) {
    const result = rule(internalValue.value);
    if (typeof result === 'string') {
      errorMessage.value = result;
      emit('validation-change', false); // Meldet Ungültigkeit
      return false;
    }
  }
  errorMessage.value = '';
  emit('validation-change', true); // Meldet Gültigkeit
  return true;
};

watch([internalValue, () => props.forceValidation, isTouched], () => {
  if (isTouched.value || props.forceValidation) {
    validate();
  } else {
    errorMessage.value = '';
  }
}, { immediate: true }); // Führt die Validierung sofort beim Komponenten-Mount aus, falls forceValidation true ist

const handleChange = (event) => {
  internalValue.value = event.target.checked;
  emit('update:modelValue', internalValue.value);
  isTouched.value = true;
};

const hasError = computed(() => (isTouched.value || props.forceValidation) && errorMessage.value !== '');
</script>